import {
  ApolloClient,
  type ApolloClientOptions,
  ApolloLink,
  type FetchResult,
  HttpLink,
  type HttpOptions,
  InMemoryCache,
  type NormalizedCacheObject,
  Observable,
  type Operation,
} from "@apollo/client";
import type { RetryLink } from "@apollo/client/link/retry";
import EventEmitter from "eventemitter3";
import { print } from "graphql";
import { type Client, type ClientOptions, createClient } from "graphql-ws";
import merge from "lodash/merge";
import { rehydrateDatesLink } from "./DateRehydration";
import {
  composeLinks,
  createAuthLink,
  createHttpErrorLink,
  createNamedOperationLink,
  createRetryLink,
  createSplitLink,
  createSubscriptionErrorLink,
  createTimeoutLink,
  type LinkErrorCallbacks,
} from "./LinkChainBuilder";
import { LoggerWrapper } from "./logger";

export type { ApolloClient };

const logger = new LoggerWrapper("ApolloConnection");

export type ApolloOptions = {
  client: Omit<ApolloClientOptions<NormalizedCacheObject>, "cache" | "link">;
  link: {
    http: HttpOptions;
    ws: ClientOptions;
    timeout: {
      timeout: number;
      statusCode?: number;
    };
    retry: RetryLink.Options;
  };
};
export type ApolloOptionsOverride = Partial<{
  client: Partial<
    Omit<ApolloClientOptions<NormalizedCacheObject>, "cache" | "link">
  >;
  link: Partial<{
    http: Partial<HttpOptions>;
    ws: Partial<ClientOptions>;
    timeout: {
      timeout: number;
      statusCode?: number;
    };
    retry: Partial<RetryLink.Options>;
  }>;
}>;

export enum ApolloEvent {
  GRAPHQL_ERROR = "graphql_error",
  NETWORK_ERROR = "network_error",
  WS_INTERNAL_ERROR = "ws_internal_error",
  WS_CONNECTING = "ws_connecting",
  WS_CONNECTED = "ws_connected",
  WS_CLOSED = "ws_closed",
  WS_TERMINATED = "ws_terminated",
  WS_GENERAL_ERROR = "ws_general_error",
  WS_NETWORK_ERROR = "ws_network_error",
  WS_GRAPHQL_ERROR = "ws_graphql_error",
}

export enum WebsocketCloseCode {
  InternalServerError = 4500,
  InternalClientError = 4005,
  BadRequest = 4400,
  BadResponse = 4004,
  /** Tried subscribing before connect ack */
  Unauthorized = 4401,
  Forbidden = 4403,
  SubprotocolNotAcceptable = 4406,
  ConnectionInitialisationTimeout = 4408,
  ConnectionAcknowledgementTimeout = 4504,
  /** Subscriber distinction is very important */
  SubscriberAlreadyExists = 4409,
  TooManyInitialisationRequests = 4429,
}

export class ApolloConnection extends EventEmitter {
  static instance?: ApolloConnection;

  private apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

  static getInstance(): ApolloConnection {
    if (!ApolloConnection.instance) {
      ApolloConnection.instance = new ApolloConnection();
    }
    return ApolloConnection.instance;
  }

  private socket: WebSocket | undefined;

  private constructor() {
    super();

    if (!ApolloConnection.instance) {
      ApolloConnection.instance = this;
    }
  }

  public init(
    options: ApolloOptions,
    getAuthToken: () => Promise<string | null>,
  ): ApolloClient<NormalizedCacheObject> {
    const existingClient = this.getApolloClient();
    if (existingClient) {
      return existingClient;
    }

    const cache = new InMemoryCache();

    // Create error callbacks that emit events
    const httpCallbacks: LinkErrorCallbacks = {
      onGraphQLError: (message) =>
        this.emit(ApolloEvent.GRAPHQL_ERROR, message),
      onNetworkError: (error) => this.emit(ApolloEvent.NETWORK_ERROR, error),
    };

    const wsCallbacks: LinkErrorCallbacks = {
      onWsGraphQLError: (message) =>
        this.emit(ApolloEvent.WS_GRAPHQL_ERROR, message),
      onWsNetworkError: (error) =>
        this.emit(ApolloEvent.WS_NETWORK_ERROR, error),
    };

    // Create WebSocket link with event handlers
    const wsLink = new WebSocketLink({
      ...options.link.ws,
      onNonLazyError: (errorOrCloseEvent): void => {
        if (errorOrCloseEvent instanceof CloseEvent) {
          const err = errorOrCloseEvent as CloseEvent;
          this.emit(ApolloEvent.WS_NETWORK_ERROR, err);
          logger.error(
            `network error (${err.code}): ${err.reason}, clean: ${err.wasClean}`,
          );
        } else {
          const err = errorOrCloseEvent as Error;
          this.emit(ApolloEvent.WS_INTERNAL_ERROR, err);
          logger.error(`internal error: ${err.message}`);
        }
      },
      on: {
        connecting: (): void => {
          logger.debug("connecting");
          this.emit(ApolloEvent.WS_CONNECTING);
        },
        connected: (socket): void => {
          this.socket = socket as WebSocket;
          logger.debug("connected");
          this.emit(ApolloEvent.WS_CONNECTED);
        },
        closed: (e): void => {
          logger.debug("closed");
          this.emit(ApolloEvent.WS_CLOSED, e as CloseEvent);
        },
        error: (e): void => {
          if (e instanceof ErrorEvent) {
            const err = e as ErrorEvent;
            this.emit(ApolloEvent.WS_GENERAL_ERROR, err);
            logger.error(`error: ${err.message}`);
          } else if (e instanceof Error) {
            const err = e as Error;
            this.emit(ApolloEvent.WS_GENERAL_ERROR, err);
            logger.error(`error: ${e}`);
          }
        },
      },
      connectionParams: async (): Promise<{ token?: string | null }> => {
        try {
          const token = await getAuthToken();
          if (token) {
            return { token };
          }
          return {};
        } catch (_e) {
          return {};
        }
      },
      shouldRetry: () => true,
    });

    // Build HTTP link chain using extracted helpers
    const httpLink = new HttpLink(options.link.http);
    const authLink = createAuthLink(async () => {
      try {
        const token = await getAuthToken();
        return token ? `Bearer ${token}` : null;
      } catch (_e) {
        return null;
      }
    });
    const timeoutLink = createTimeoutLink(
      options.link.timeout.timeout,
      options.link.timeout.statusCode,
    );
    const retryLink = createRetryLink(options.link.retry);
    const httpErrorLink = createHttpErrorLink(httpCallbacks, logger);
    const subscriptionErrorLink = createSubscriptionErrorLink(
      wsCallbacks,
      logger,
    );
    const namedLink = createNamedOperationLink(options.link.http.uri as string);

    // Compose the HTTP link chain
    const httpChain = composeLinks([
      timeoutLink,
      retryLink,
      httpErrorLink,
      rehydrateDatesLink,
      namedLink,
      authLink,
      httpLink,
    ]);

    // Compose the subscription link chain
    const subscriptionChain = composeLinks([
      subscriptionErrorLink,
      rehydrateDatesLink,
      wsLink,
    ]);

    // Split between subscriptions and queries/mutations
    const splitLink = createSplitLink(subscriptionChain, httpChain);

    this.apolloClient = new ApolloClient({
      ...options,
      cache,
      link: splitLink,
    });

    return this.apolloClient;
  }

  public dispose(): void {
    this.apolloClient?.stop();
  }

  public getApolloClient(): ApolloClient<NormalizedCacheObject> | undefined {
    return this.apolloClient;
  }

  public closeWebsocket(): boolean {
    if (this.socket) {
      this.socket.close(4205, "Client Restart");
      return true;
    }
    return false;
  }
}

class WebSocketLink extends ApolloLink {
  private client: Client;

  private timedOut: ReturnType<typeof setTimeout> | undefined;

  constructor(options: ClientOptions) {
    super();

    this.client = createClient(
      merge<ClientOptions, Partial<ClientOptions>>(options, {
        on: {
          ping: (received) => {
            if (!received /* sent */) {
              this.timedOut = setTimeout(() => {
                // a close event `4499: Terminated` is issued to the current WebSocket and an
                // artificial `{ code: 4499, reason: 'Terminated', wasClean: false }` close-event-like
                // object is immediately emitted without waiting for the one coming from `WebSocket.onclose`
                //
                // calling terminate is not considered fatal and a connection retry will occur as expected
                //
                // see: https://github.com/enisdenjo/graphql-ws/discussions/290
                this.client.terminate();
              }, 5_000);
            }
          },
          pong: (received) => {
            if (received && this.timedOut) {
              clearTimeout(this.timedOut);
            }
          },
        },
      }),
    );
  }

  public request(operation: Operation): Observable<FetchResult> {
    return new Observable((sink) => {
      return this.client.subscribe<FetchResult>(
        { ...operation, query: print(operation.query) },
        {
          // biome-ignore lint/suspicious/noExplicitAny: legacy code
          next: sink.next.bind(sink) as any,
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink),
        },
      );
    });
  }
}

export const apolloConnection = ApolloConnection.getInstance();

// biome-ignore lint/suspicious/noExplicitAny: legacy code
(globalThis as any).ac = apolloConnection;
